<template>
  <div class="account-settings-page">
    <Header />
    <div class="content-wrapper">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8 col-md-10">
            <div class="settings-card">
              <div class="settings-header">
                <h1 class="settings-title">Account Settings</h1>
                <p class="settings-subtitle">Manage your account information and preferences</p>
              </div>

              <!-- Account Information -->
              <div class="settings-section">
                <h2 class="section-title">Account Information</h2>
                <div v-if="loading" class="loading-state">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p>Loading account information...</p>
                </div>
                <div v-else class="info-group">
                  <div class="info-item">
                    <span class="info-label">Email:</span>
                    <span class="info-value">{{ userEmail }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Account Type:</span>
                    <span class="info-value">{{ displayAccountType }}</span>
                  </div>
                  <div v-if="userInfo.company" class="info-item">
                    <span class="info-label">Company:</span>
                    <span class="info-value">{{ userInfo.company }}</span>
                  </div>
                  <div v-if="userInfo.firstName || userInfo.lastName" class="info-item">
                    <span class="info-label">Name:</span>
                    <span class="info-value">{{ fullName }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">Roles:</span>
                    <span class="info-value">{{ userRoles }}</span>
                  </div>
                </div>
              </div>

              <!-- Danger Zone -->
              <div class="settings-section danger-zone">
                <h2 class="section-title text-danger">Danger Zone</h2>
                <div class="danger-content">
                  <div class="danger-description">
                    <h4>Delete Account</h4>
                    <p>Once you delete your account, there is no going back. All your data will be permanently removed.</p>
                  </div>
                  <button 
                    class="btn btn-danger"
                    @click="showDeleteModal = true"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />

    <!-- Delete Account Modal -->
    <DeleteAccountModal 
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';
import Header from '../components/Header.vue';
import Footer from '../components/Footer.vue';
import DeleteAccountModal from '../components/DeleteAccountModal.vue';
import axios from 'axios';

export default {
  name: 'AccountSettingsView',
  components: {
    Header,
    Footer,
    DeleteAccountModal
  },
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const showDeleteModal = ref(false);
    const userEmail = ref('');
    const userInfo = ref({});
    const loading = ref(true);

    // Redirect if not logged in
    onMounted(async () => {
      if (!authStore.isLoggedIn) {
        router.push('/login');
        return;
      }

      // Fetch user information
      try {
        const response = await axios.get('/user/info');
        userInfo.value = response.data;
        userEmail.value = response.data.email;
        loading.value = false;
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        userEmail.value = 'Unable to load';
        loading.value = false;
      }
    });

    // Computed properties
    const displayAccountType = computed(() => {
      const type = userInfo.value.accountType || 'individual';
      return type === 'individual' ? 'Private Person' : 'Company';
    });

    const fullName = computed(() => {
      const firstName = userInfo.value.firstName || '';
      const lastName = userInfo.value.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'Not provided';
    });

    const userRoles = computed(() => {
      const roles = userInfo.value.roles || [];
      return roles.length > 0 ? roles.join(', ') : 'User';
    });

    const handleDeleteConfirm = async () => {
      try {
        await authStore.deleteAccount();
        showDeleteModal.value = false;
        // The auth store will handle logout and redirect
      } catch (error) {
        console.error('Failed to delete account:', error);
        alert('Failed to delete account. Please try again later.');
      }
    };

    return {
      showDeleteModal,
      userEmail,
      userInfo,
      loading,
      displayAccountType,
      fullName,
      userRoles,
      handleDeleteConfirm
    };
  }
};
</script>

<style scoped>
.account-settings-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  flex: 1;
  padding-top: 120px;
  padding-bottom: 60px;
  background-color: #f8f9fa;
}

.settings-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.settings-header {
  padding: 2rem;
  background: linear-gradient(135deg, #87b140 0%, #6b8f33 100%);
  color: white;
  text-align: center;
}

.settings-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.settings-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.settings-section {
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
}

.settings-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #333;
}

.info-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.info-label {
  font-weight: 600;
  color: #666;
  min-width: 120px;
}

.info-value {
  color: #333;
  font-size: 1.1rem;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  color: #666;
}

/* Danger Zone Styling */
.danger-zone {
  background-color: #fff5f5;
  border: 1px solid #fee;
}

.danger-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.danger-description h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #dc3545;
  margin-bottom: 0.5rem;
}

.danger-description p {
  color: #666;
  margin: 0;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

@media (max-width: 768px) {
  .content-wrapper {
    padding-top: 100px;
    padding-bottom: 40px;
  }

  .settings-header {
    padding: 1.5rem;
  }

  .settings-title {
    font-size: 1.5rem;
  }

  .settings-subtitle {
    font-size: 1rem;
  }

  .danger-content {
    flex-direction: column;
    text-align: center;
  }

  .info-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
}
</style>